/**
 * SETTINGS MODAL - Planivio v3.0
 * 
 * ✅ v3.0.0: PLANIVIO REBRANDING
 *    - API URLs: homem.app → planivio.de
 *    - Brand: HomeM → Planivio
 *    - Farben: Orange (#F97316) → Teal (#4A90A4)
 * 
 * ✅ v2.6.0: 3 SPRACHEN SUPPORT (DE/EN/FR)
 * ✅ v2.5.0: HOUSEHOLD MEMBERS MIT EDITIERBAREN NAMEN
 * 
 * @version 3.0.0 - Planivio Rebranding
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import AccountSettings from './AccountSettings';
import { useTranslation, LANGUAGES } from '../../context/I18nContext';
import './SettingsModal.css';

// ✅ v3: Planivio API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// App Version Info
const APP_VERSION = '3.0.0';
const BUILD_NUMBER = '2026.01.04';
const BUILD_ENV = process.env.NODE_ENV || 'production';

const SettingsModal = ({ 
  isOpen, 
  onClose, 
  currentUser,
  onLogout,
  onNavigate 
}) => {
  const { language, setLanguage, currentLanguage } = useTranslation();
  const { getAccessToken } = useAuth();
  
  // Local State
  const [darkMode, setDarkMode] = useState(false);
  const [showLanguageSelect, setShowLanguageSelect] = useState(false);
  
  // Household State
  const [household, setHousehold] = useState(null);
  const [householdLoading, setHouseholdLoading] = useState(false);
  const [showJoinForm, setShowJoinForm] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinError, setJoinError] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);
  
  // ✅ NEU: Household Members State
  const [householdUsers, setHouseholdUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [editForm, setEditForm] = useState({ display_name: '', email: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [myRole, setMyRole] = useState('member');
  
  // Delete Account - 2-Step Confirmation
  const [deleteStep, setDeleteStep] = useState(0);
  const [deleteInput, setDeleteInput] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Account Settings Modal
  const [showAccountSettings, setShowAccountSettings] = useState(false);

  // ===========================
  // EINGEBAUTE TEXTE (3 SPRACHEN)
  // ===========================
  const texts = {
    de: {
      title: 'Einstellungen',
      account: 'Konto',
      accountSecurity: 'Konto & Sicherheit',
      accountSecurityDesc: 'Profil, Passwort verwalten',
      myHousehold: 'Mein Haushalt',
      manage: 'Verwalten',
      appSettings: 'App-Einstellungen',
      language: 'Sprache',
      darkMode: 'Dunkler Modus',
      helpSupport: 'Hilfe & Support',
      feedback: 'Feedback',
      legal: 'Rechtliches',
      legalDesc: 'AGB, Datenschutz, Impressum',
      logout: 'Abmelden',
      noHousehold: 'Noch kein Haushalt',
      noHouseholdDesc: 'Erstelle einen Haushalt oder tritt einem bei',
      create: 'Erstellen',
      join: 'Beitreten',
      inviteCode: 'Einladungscode',
      shareCodeHint: 'Teile diesen Code mit Familienmitgliedern',
      loading: 'Lade...',
      member: 'Mitglied',
      members: 'Mitglieder',
      admin: 'Admin',
      owner: 'Ersteller',
      pleaseWait: 'Bitte warten...',
      deleteAccountSection: 'Konto löschen',
      deleteAccount: 'Konto löschen',
      deleteAccountDesc: 'Alle Daten unwiderruflich löschen',
      deleteWarningTitle: 'Konto wirklich löschen?',
      deleteWarningText: 'Wenn du dein Konto löschst, werden alle deine Daten unwiderruflich gelöscht. Das betrifft:',
      deleteWarningList: ['Einkaufslisten', 'Rezepte', 'Notizen', 'Kalendereinträge', 'Budget-Einträge', 'Haushaltsmitgliedschaften'],
      deleteWarningContinue: 'Ja, Konto löschen',
      deleteConfirmTitle: 'Endgültige Bestätigung',
      deleteConfirmText: 'Diese Aktion kann nicht rückgängig gemacht werden. Dein Konto und alle Daten werden sofort gelöscht.',
      deleteConfirmInput: 'Gib "LÖSCHEN" ein zur Bestätigung',
      cancel: 'Abbrechen',
      back: 'Zurück',
      build: 'Build',
      you: 'Du',
      edit: 'Bearbeiten',
      save: 'Speichern',
      namePlaceholder: 'Name eingeben',
      emailPlaceholder: 'Email (optional)',
      nameRequired: 'Name muss mindestens 2 Zeichen haben',
      saved: 'Gespeichert!',
      errorSaving: 'Fehler beim Speichern',
      confirmRemove: 'Mitglied wirklich entfernen?',
    },
    en: {
      title: 'Settings',
      account: 'Account',
      accountSecurity: 'Account & Security',
      accountSecurityDesc: 'Manage profile, password',
      myHousehold: 'My Household',
      manage: 'Manage',
      appSettings: 'App Settings',
      language: 'Language',
      darkMode: 'Dark Mode',
      helpSupport: 'Help & Support',
      feedback: 'Feedback',
      legal: 'Legal',
      legalDesc: 'Terms, Privacy, Imprint',
      logout: 'Logout',
      noHousehold: 'No household yet',
      noHouseholdDesc: 'Create a household or join one',
      create: 'Create',
      join: 'Join',
      inviteCode: 'Invite Code',
      shareCodeHint: 'Share this code with family members',
      loading: 'Loading...',
      member: 'Member',
      members: 'Members',
      admin: 'Admin',
      owner: 'Owner',
      pleaseWait: 'Please wait...',
      deleteAccountSection: 'Delete Account',
      deleteAccount: 'Delete Account',
      deleteAccountDesc: 'Permanently delete all data',
      deleteWarningTitle: 'Really delete account?',
      deleteWarningText: 'If you delete your account, all your data will be permanently deleted. This includes:',
      deleteWarningList: ['Shopping lists', 'Recipes', 'Notes', 'Calendar entries', 'Budget entries', 'Household memberships'],
      deleteWarningContinue: 'Yes, delete account',
      deleteConfirmTitle: 'Final Confirmation',
      deleteConfirmText: 'This action cannot be undone. Your account and all data will be deleted immediately.',
      deleteConfirmInput: 'Type "DELETE" to confirm',
      cancel: 'Cancel',
      back: 'Back',
      build: 'Build',
      you: 'You',
      edit: 'Edit',
      save: 'Save',
      namePlaceholder: 'Enter name',
      emailPlaceholder: 'Email (optional)',
      nameRequired: 'Name must be at least 2 characters',
      saved: 'Saved!',
      errorSaving: 'Error saving',
      confirmRemove: 'Really remove member?',
    },
    fr: {
      title: 'Paramètres',
      account: 'Compte',
      accountSecurity: 'Compte & Sécurité',
      accountSecurityDesc: 'Gérer le profil, mot de passe',
      myHousehold: 'Mon Foyer',
      manage: 'Gérer',
      appSettings: 'Paramètres',
      language: 'Langue',
      darkMode: 'Mode Sombre',
      helpSupport: 'Aide & Support',
      feedback: 'Feedback',
      legal: 'Mentions légales',
      legalDesc: 'CGV, Confidentialité, Mentions',
      logout: 'Déconnexion',
      noHousehold: 'Pas encore de foyer',
      noHouseholdDesc: 'Créez un foyer ou rejoignez-en un',
      create: 'Créer',
      join: 'Rejoindre',
      inviteCode: "Code d'invitation",
      shareCodeHint: 'Partagez ce code avec les membres de la famille',
      loading: 'Chargement...',
      member: 'Membre',
      members: 'Membres',
      admin: 'Admin',
      owner: 'Créateur',
      pleaseWait: 'Veuillez patienter...',
      deleteAccountSection: 'Supprimer le compte',
      deleteAccount: 'Supprimer le compte',
      deleteAccountDesc: 'Supprimer définitivement toutes les données',
      deleteWarningTitle: 'Vraiment supprimer le compte?',
      deleteWarningText: 'Si vous supprimez votre compte, toutes vos données seront définitivement supprimées. Cela inclut:',
      deleteWarningList: ['Listes de courses', 'Recettes', 'Notes', 'Événements', 'Entrées budget', 'Adhésions au foyer'],
      deleteWarningContinue: 'Oui, supprimer le compte',
      deleteConfirmTitle: 'Confirmation finale',
      deleteConfirmText: 'Cette action est irréversible. Votre compte et toutes vos données seront supprimés immédiatement.',
      deleteConfirmInput: 'Tapez "SUPPRIMER" pour confirmer',
      cancel: 'Annuler',
      back: 'Retour',
      build: 'Build',
      you: 'Vous',
      edit: 'Modifier',
      save: 'Enregistrer',
      namePlaceholder: 'Entrez le nom',
      emailPlaceholder: 'Email (optionnel)',
      nameRequired: 'Le nom doit avoir au moins 2 caractères',
      saved: 'Enregistré!',
      errorSaving: "Erreur d'enregistrement",
      confirmRemove: 'Vraiment supprimer ce membre?',
    }
  };

  const getText = (key) => {
    const lang = language || 'de';
    return texts[lang]?.[key] || texts.de[key] || key;
  };

  // ✅ NEU: Helper für Bestätigungswort bei Löschung
  const getDeleteConfirmWord = () => {
    if (language === 'en') return 'DELETE';
    if (language === 'fr') return 'SUPPRIMER';
    return 'LÖSCHEN';
  };

  // ===========================
  // API HELPER
  // ===========================
  const apiCall = useCallback(async (endpoint, options = {}) => {
    // ✅ v3: Planivio API
    const token = await getAccessToken(process.env.REACT_APP_LOGTO_API_RESOURCE || process.env.REACT_APP_API_URL);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });
    
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || `Error ${response.status}`);
    }
    return data;
  }, [getAccessToken]);

  // ===========================
  // LOAD DATA
  // ===========================
  useEffect(() => {
    if (isOpen) {
      loadHousehold();
    }
  }, [isOpen]);

  const loadHousehold = async () => {
    try {
      setHouseholdLoading(true);
      const data = await apiCall('/api/household');
      setHousehold(data.household);
      
      // Wenn Haushalt vorhanden, lade die Mitglieder
      if (data.household) {
        loadHouseholdUsers();
      }
    } catch (err) {
      console.error('Error loading household:', err);
      setHousehold(null);
    } finally {
      setHouseholdLoading(false);
    }
  };

  // ✅ NEU: Lade Haushaltsmitglieder
  const loadHouseholdUsers = async () => {
    try {
      setUsersLoading(true);
      const data = await apiCall('/api/household/users');
      
      if (data.success) {
        setHouseholdUsers(data.users);
        // Finde eigene Rolle
        const me = data.users.find(u => u.is_me);
        if (me) setMyRole(me.role);
      }
    } catch (err) {
      console.error('Error loading household users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  // ===========================
  // ✅ NEU: MEMBER PROFILE HANDLERS
  // ===========================
  
  // Edit starten
  const startEditMember = (user) => {
    setEditingMemberId(user.id);
    setEditForm({
      display_name: user.display_name || '',
      email: user.email || ''
    });
  };

  const cancelEditMember = () => {
    setEditingMemberId(null);
    setEditForm({ display_name: '', email: '' });
  };

  // Profil speichern
  const saveMemberProfile = async (memberId) => {
    if (!editForm.display_name.trim() || editForm.display_name.trim().length < 2) {
      alert(getText('nameRequired'));
      return;
    }
    
    try {
      setSavingProfile(true);
      
      const data = await apiCall(`/api/household/users/${memberId}/profile`, {
        method: 'PUT',
        body: JSON.stringify({
          display_name: editForm.display_name.trim(),
          email: editForm.email.trim() || null
        })
      });
      
      if (data.success) {
        // Update lokale Liste
        setHouseholdUsers(householdUsers.map(u => 
          u.id === memberId 
            ? { ...u, display_name: data.user.display_name, email: data.user.email }
            : u
        ));
        setEditingMemberId(null);
      }
    } catch (err) {
      console.error('Error saving profile:', err);
      alert(getText('errorSaving') + ': ' + err.message);
    } finally {
      setSavingProfile(false);
    }
  };

  // Rolle ändern
  const changeMemberRole = async (memberId, newRole) => {
    try {
      await apiCall(`/api/household/users/${memberId}/role`, {
        method: 'PUT',
        body: JSON.stringify({ role: newRole })
      });
      
      // Update lokale Liste
      setHouseholdUsers(householdUsers.map(u => 
        u.id === memberId ? { ...u, role: newRole } : u
      ));
    } catch (err) {
      console.error('Error changing role:', err);
      alert(err.message);
    }
  };

  // Mitglied entfernen
  const removeMember = async (memberId) => {
    if (!window.confirm(getText('confirmRemove'))) return;
    
    try {
      await apiCall(`/api/household/users/${memberId}`, {
        method: 'DELETE'
      });
      
      // Update lokale Liste
      setHouseholdUsers(householdUsers.filter(u => u.id !== memberId));
      // Update member count
      if (household) {
        setHousehold({ ...household, member_count: household.member_count - 1 });
      }
    } catch (err) {
      console.error('Error removing member:', err);
      alert(err.message);
    }
  };

  // Helper: Anzeigename ermitteln
  const getDisplayName = (user) => {
    if (user.display_name && user.display_name !== 'Mitglied' && user.display_name !== 'Admin') {
      return user.display_name;
    }
    if (user.email) {
      return user.email.split('@')[0];
    }
    return `User-${user.user_id.substring(0, 6)}`;
  };

  // Kann bearbeiten?
  const canEditMember = (user) => {
    if (user.is_me) return true;
    return myRole === 'admin';
  };

  // ✅ v3: Planivio Teal Avatar Farben
  const getMemberAvatarColor = (user) => {
    if (user.is_owner) return '#4A90A4';
    if (user.role === 'admin') return '#6BA893';
    return '#3B82F6';
  };

  // Avatar Icon
  const getMemberAvatarIcon = (user) => {
    if (user.is_owner) return '👑';
    if (user.role === 'admin') return '⭐';
    return '👤';
  };

  // ===========================
  // HANDLERS
  // ===========================
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    setShowLanguageSelect(false);
  };

  const handleDarkModeToggle = () => {
    setDarkMode(!darkMode);
  };

  const handleLogout = () => {
    onClose();
    onLogout();
  };

  const handleNavigateToPage = (page) => {
    onClose();
    if (onNavigate) {
      onNavigate(page);
    } else {
      window.location.href = `/${page}`;
    }
  };

  const handleOpenLogtoAccount = () => {
    setShowAccountSettings(true);
  };

  // DELETE ACCOUNT
  const handleDeleteAccount = async () => {
    const confirmWord = getDeleteConfirmWord();
    if (deleteInput !== confirmWord || deleteLoading) return;
    
    try {
      setDeleteLoading(true);
      
      const result = await apiCall('/api/account', {
        method: 'DELETE'
      });
      
      console.log('✅ Account deleted:', result);
      
      const successMsg = {
        de: '✅ Dein Konto wurde erfolgreich gelöscht.\n\nDu wirst jetzt abgemeldet.',
        en: '✅ Your account has been successfully deleted.\n\nYou will now be logged out.',
        fr: '✅ Votre compte a été supprimé avec succès.\n\nVous allez être déconnecté.'
      };
      alert(successMsg[language] || successMsg.de);
      
      setDeleteStep(0);
      setDeleteInput('');
      onClose();
      onLogout();
      
    } catch (err) {
      console.error('❌ Delete account error:', err);
      const errorPrefix = { de: 'Fehler: ', en: 'Error: ', fr: 'Erreur: ' };
      alert((errorPrefix[language] || errorPrefix.de) + err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteStep(0);
    setDeleteInput('');
  };

  // HOUSEHOLD HANDLERS
  const handleCopyCode = async () => {
    if (!household?.invite_code) return;
    try {
      await navigator.clipboard.writeText(household.invite_code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch (err) {
      const input = document.createElement('input');
      input.value = household.invite_code;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  const handleShareCode = async () => {
    if (!household?.invite_code) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Tritt "${household.name}" bei!`,
          text: `Einladungscode: ${household.invite_code}`,
        });
      } catch (err) {
        handleCopyCode();
      }
    } else {
      handleCopyCode();
    }
  };

  const handleJoinHousehold = async (e) => {
    e.preventDefault();
    if (joinCode.length !== 8 || joinLoading) return;
    
    try {
      setJoinLoading(true);
      setJoinError(null);
      const result = await apiCall('/api/household/join', {
        method: 'POST',
        body: JSON.stringify({ code: joinCode.trim().toUpperCase() })
      });
      setHousehold(result.household);
      setShowJoinForm(false);
      setJoinCode('');
      // Lade Mitglieder nach Beitritt
      loadHouseholdUsers();
    } catch (err) {
      setJoinError(err.message);
    } finally {
      setJoinLoading(false);
    }
  };

  const handleCreateHousehold = async () => {
    const promptMsg = {
      de: 'Name für deinen Haushalt:',
      en: 'Name for your household:',
      fr: 'Nom de votre foyer:'
    };
    const defaultName = {
      de: 'Mein Haushalt',
      en: 'My Household',
      fr: 'Mon Foyer'
    };
    const name = prompt(promptMsg[language] || promptMsg.de, defaultName[language] || defaultName.de);
    if (!name || name.trim().length < 2) return;
    
    try {
      setHouseholdLoading(true);
      const result = await apiCall('/api/household', {
        method: 'POST',
        body: JSON.stringify({ name: name.trim() })
      });
      setHousehold(result.household);
      
      const successMsg = {
        de: `✅ Haushalt "${result.household.name}" erstellt!\n\nEinladungscode: ${result.household.invite_code}`,
        en: `✅ Household "${result.household.name}" created!\n\nInvite code: ${result.household.invite_code}`,
        fr: `✅ Foyer "${result.household.name}" créé!\n\nCode d'invitation: ${result.household.invite_code}`
      };
      alert(successMsg[language] || successMsg.de);
      // Lade Mitglieder nach Erstellung
      loadHouseholdUsers();
    } catch (err) {
      const errorPrefix = { de: 'Fehler: ', en: 'Error: ', fr: 'Erreur: ' };
      alert((errorPrefix[language] || errorPrefix.de) + err.message);
    } finally {
      setHouseholdLoading(false);
    }
  };

  // ===========================
  // EARLY RETURN IF NOT OPEN
  // ===========================
  if (!isOpen) return null;

  // ===========================
  // RENDER
  // ===========================
  return (
    <div className="settings-modal-overlay" onClick={handleOverlayClick}>
      <div className="settings-modal">
        
        {/* HEADER */}
        <div className="settings-modal-header">
          <div className="settings-modal-title">
            <span>⚙️</span>
            <span>{getText('title')}</span>
          </div>
          <button className="settings-modal-close" onClick={onClose}>✕</button>
        </div>

        {/* BODY */}
        <div className="settings-modal-body">

          {/* PROFIL SECTION */}
          <div className="settings-profile-section">
            <div className="settings-avatar-wrapper">
              <div className="settings-avatar">
                {currentUser?.avatar ? (
                  <img src={currentUser.avatar} alt="Avatar" />
                ) : (
                  currentUser?.name?.charAt(0)?.toUpperCase() || '👤'
                )}
              </div>
            </div>
            <div className="settings-profile-info">
              <div className="settings-profile-name">
                {currentUser?.name || 'User'}
              </div>
              <div className="settings-profile-email">{currentUser?.email || 'email@example.com'}</div>
              <div className="settings-profile-role">
                <span>{household?.my_role === 'admin' ? '👑' : '👤'}</span>
                <span>{household?.my_role === 'admin' ? getText('admin') : getText('member')}</span>
              </div>
            </div>
          </div>

          {/* KONTO */}
          <div className="settings-section">
            <div className="settings-section-title">{getText('account')}</div>
            <div className="settings-card">
              <div 
                className="settings-item" 
                onClick={handleOpenLogtoAccount}
                style={{ 
                  background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)',
                  borderRadius: '12px'
                }}
              >
                <div className="settings-item-left">
                  <div className="settings-item-icon" style={{ background: '#4F46E5' }}>🔐</div>
                  <div>
                    <div className="settings-item-label" style={{ color: '#3730A3' }}>
                      {getText('accountSecurity')}
                    </div>
                    <div className="settings-item-sublabel">{getText('accountSecurityDesc')}</div>
                  </div>
                </div>
                <div className="settings-item-right">
                  <span style={{ 
                    fontSize: '0.7rem', 
                    background: '#4F46E5',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    marginRight: '8px'
                  }}>Logto</span>
                  <span className="settings-item-arrow">↗</span>
                </div>
              </div>
            </div>
          </div>

          {/* HAUSHALT */}
          <div className="settings-section">
            <div className="settings-section-title">{getText('myHousehold')}</div>
            
            {householdLoading ? (
              <div className="settings-household-card" style={{ justifyContent: 'center' }}>
                <span style={{ color: '#6B7280' }}>{getText('loading')}</span>
              </div>
            ) : household ? (
              <>
                {/* Haushalt Info Card */}
                <div className="settings-household-card">
                  <div className="settings-household-icon">🏠</div>
                  <div className="settings-household-info">
                    <div className="settings-household-name">{household.name}</div>
                    <div className="settings-household-members">
                      {household.member_count} {household.member_count === 1 ? getText('member') : getText('members')}
                      {household.my_role === 'admin' && (
                        <span style={{ 
                          marginLeft: '8px', 
                          // ✅ v3: Planivio Teal
                          background: '#D4E8EC', 
                          padding: '2px 6px', 
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          color: '#4A90A4'
                        }}>
                          👑 {getText('admin')}
                        </span>
                      )}
                    </div>
                  </div>
                  <button 
                    className="settings-household-btn"
                    onClick={() => handleNavigateToPage('household')}
                  >
                    {getText('manage')}
                  </button>
                </div>
                
                {/* ✅ v3: Planivio Teal Mitglieder-Liste */}
                <div style={{
                  marginTop: '16px',
                  padding: '16px',
                  background: '#E8F4F8',
                  borderRadius: '12px',
                  border: '2px solid #B8D4DC'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    marginBottom: '12px' 
                  }}>
                    <div style={{ 
                      fontSize: '0.9rem', 
                      fontWeight: '600', 
                      color: '#0C3647',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>👥</span>
                      <span>{getText('members')}</span>
                      <span style={{
                        background: '#D4E8EC',
                        color: '#4A90A4',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                      }}>{householdUsers.length}</span>
                    </div>
                  </div>
                  
                  {usersLoading ? (
                    <div style={{ textAlign: 'center', padding: '1rem', color: '#6B7280' }}>
                      ⏳ {getText('loading')}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {householdUsers.map(user => (
                        <div 
                          key={user.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '12px',
                            // ✅ v3: Planivio Teal
                            background: user.is_me ? '#D4E8EC' : 'white',
                            border: user.is_me ? '2px solid #4A90A4' : '1px solid #B8D4DC',
                            borderRadius: '10px',
                            transition: 'all 0.2s'
                          }}
                        >
                          {/* Avatar */}
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: getMemberAvatarColor(user),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.2rem',
                            flexShrink: 0
                          }}>
                            {getMemberAvatarIcon(user)}
                          </div>
                          
                          {/* Content */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {editingMemberId === user.id ? (
                              // ═══ EDIT MODE ═══
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <input
                                  type="text"
                                  placeholder={getText('namePlaceholder')}
                                  value={editForm.display_name}
                                  onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                                  style={{
                                    padding: '8px 10px',
                                    // ✅ v3: Planivio Teal
                                    border: '2px solid #4A90A4',
                                    borderRadius: '6px',
                                    fontSize: '0.9rem',
                                    outline: 'none'
                                  }}
                                  autoFocus
                                />
                                <input
                                  type="email"
                                  placeholder={getText('emailPlaceholder')}
                                  value={editForm.email}
                                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                  style={{
                                    padding: '8px 10px',
                                    border: '1px solid #D1D5DB',
                                    borderRadius: '6px',
                                    fontSize: '0.85rem',
                                    outline: 'none'
                                  }}
                                />
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  <button
                                    onClick={() => saveMemberProfile(user.id)}
                                    disabled={savingProfile}
                                    style={{
                                      padding: '6px 12px',
                                      background: '#10B981',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      fontSize: '0.8rem',
                                      fontWeight: '600',
                                      cursor: savingProfile ? 'wait' : 'pointer'
                                    }}
                                  >
                                    {savingProfile ? '⏳' : '✓'} {getText('save')}
                                  </button>
                                  <button
                                    onClick={cancelEditMember}
                                    disabled={savingProfile}
                                    style={{
                                      padding: '6px 12px',
                                      background: '#E5E7EB',
                                      color: '#374151',
                                      border: 'none',
                                      borderRadius: '6px',
                                      fontSize: '0.8rem',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    ✕ {getText('cancel')}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              // ═══ VIEW MODE ═══
                              <>
                                <div style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '6px',
                                  flexWrap: 'wrap'
                                }}>
                                  <span style={{ 
                                    fontWeight: '600', 
                                    color: '#1F2937',
                                    fontSize: '0.95rem'
                                  }}>
                                    {getDisplayName(user)}
                                  </span>
                                  {user.is_me && (
                                    <span style={{ 
                                      fontSize: '0.75rem', 
                                      // ✅ v3: Planivio Teal
                                      color: '#4A90A4',
                                      fontWeight: '500'
                                    }}>({getText('you')})</span>
                                  )}
                                  {user.is_owner && (
                                    <span style={{
                                      fontSize: '0.65rem',
                                      // ✅ v3: Planivio Teal
                                      background: '#D4E8EC',
                                      color: '#4A90A4',
                                      padding: '2px 6px',
                                      borderRadius: '4px'
                                    }}>👑</span>
                                  )}
                                </div>
                                {user.email && (
                                  <div style={{ 
                                    fontSize: '0.8rem', 
                                    color: '#6B7280',
                                    marginTop: '2px'
                                  }}>{user.email}</div>
                                )}
                                {!user.display_name && !user.email && (
                                  <div style={{
                                    fontSize: '0.7rem',
                                    color: '#9CA3AF',
                                    fontFamily: 'monospace'
                                  }}>ID: {user.user_id.substring(0, 10)}...</div>
                                )}
                              </>
                            )}
                          </div>
                          
                          {/* Actions */}
                          {editingMemberId !== user.id && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                              {/* Role Dropdown (nur Admin, nicht für Owner/sich selbst) */}
                              {!user.is_me && !user.is_owner && myRole === 'admin' && (
                                <select
                                  value={user.role}
                                  onChange={(e) => changeMemberRole(user.id, e.target.value)}
                                  style={{
                                    padding: '4px 8px',
                                    border: '1px solid #D1D5DB',
                                    borderRadius: '6px',
                                    fontSize: '0.75rem',
                                    background: 'white',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <option value="admin">{getText('admin')}</option>
                                  <option value="member">{getText('member')}</option>
                                </select>
                              )}
                              
                              {/* Edit Button */}
                              {canEditMember(user) && (
                                <button
                                  onClick={() => startEditMember(user)}
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    background: '#E5E7EB',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                  title={getText('edit')}
                                >
                                  ✏️
                                </button>
                              )}
                              
                              {/* Delete Button (nur Admin, nicht für Owner/sich selbst) */}
                              {myRole === 'admin' && !user.is_me && !user.is_owner && (
                                <button
                                  onClick={() => removeMember(user.id)}
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    background: '#FEE2E2',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    color: '#DC2626',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* ✅ v3: Planivio Teal Invite Code Section (nur Admin) */}
                {household.my_role === 'admin' && household.invite_code && (
                  <div style={{
                    marginTop: '12px',
                    padding: '16px',
                    background: '#E8F4F8',
                    borderRadius: '12px',
                    border: '1px solid #B8D4DC'
                  }}>
                    <div style={{ fontSize: '0.85rem', color: '#0C3647', marginBottom: '8px', fontWeight: 600 }}>
                      📨 {getText('inviteCode')}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <code style={{
                        flex: 1,
                        padding: '10px 14px',
                        background: 'white',
                        // ✅ v3: Planivio Teal
                        border: '2px solid #B8D4DC',
                        borderRadius: '8px',
                        fontFamily: 'monospace',
                        fontSize: '1.25rem',
                        letterSpacing: '3px',
                        textAlign: 'center',
                        fontWeight: 700,
                        color: '#4A90A4'
                      }}>
                        {household.invite_code}
                      </code>
                      <button onClick={handleCopyCode} style={{
                        padding: '10px 12px',
                        background: 'white',
                        border: '2px solid #B8D4DC',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '1.1rem'
                      }}>{codeCopied ? '✅' : '📋'}</button>
                      <button onClick={handleShareCode} style={{
                        padding: '10px 12px',
                        background: 'white',
                        border: '2px solid #B8D4DC',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '1.1rem'
                      }}>📤</button>
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#2C6B77', marginTop: '8px' }}>
                      {getText('shareCodeHint')}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{
                padding: '20px',
                background: '#F9FAFB',
                borderRadius: '12px',
                border: '2px dashed #D1D5DB',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>🏠</div>
                <div style={{ fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                  {getText('noHousehold')}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#6B7280', marginBottom: '16px' }}>
                  {getText('noHouseholdDesc')}
                </div>
                
                {showJoinForm ? (
                  <form onSubmit={handleJoinHousehold} style={{ textAlign: 'left' }}>
                    <input
                      type="text"
                      value={joinCode}
                      onChange={(e) => setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                      placeholder="ABCD1234"
                      maxLength={8}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: joinError ? '2px solid #EF4444' : '2px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '1.25rem',
                        fontFamily: 'monospace',
                        letterSpacing: '4px',
                        textAlign: 'center',
                        marginBottom: '8px',
                        boxSizing: 'border-box'
                      }}
                      autoFocus
                    />
                    {joinError && (
                      <div style={{ color: '#DC2626', fontSize: '0.85rem', marginBottom: '8px' }}>
                        ❌ {joinError}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="submit"
                        disabled={joinCode.length !== 8 || joinLoading}
                        style={{
                          flex: 1,
                          padding: '10px',
                          // ✅ v3: Planivio Teal
                          background: joinCode.length === 8 ? 'linear-gradient(135deg, #4A90A4, #6BA893)' : '#D1D5DB',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontWeight: 600,
                          cursor: joinCode.length === 8 ? 'pointer' : 'not-allowed'
                        }}
                      >
                        {joinLoading ? `⏳ ${getText('pleaseWait')}` : `🔗 ${getText('join')}`}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowJoinForm(false); setJoinCode(''); setJoinError(null); }}
                        style={{
                          padding: '10px 16px',
                          background: '#F3F4F6',
                          color: '#374151',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer'
                        }}
                      >✕</button>
                    </div>
                  </form>
                ) : (
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                    <button
                      onClick={handleCreateHousehold}
                      disabled={householdLoading}
                      style={{
                        padding: '10px 20px',
                        // ✅ v3: Planivio Teal
                        background: 'linear-gradient(135deg, #4A90A4, #6BA893)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(74, 144, 164, 0.3)'
                      }}
                    >➕ {getText('create')}</button>
                    <button
                      onClick={() => setShowJoinForm(true)}
                      style={{
                        padding: '10px 20px',
                        background: 'white',
                        color: '#374151',
                        border: '2px solid #D1D5DB',
                        borderRadius: '8px',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >🔗 {getText('join')}</button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* APP-EINSTELLUNGEN */}
          <div className="settings-section">
            <div className="settings-section-title">{getText('appSettings')}</div>
            <div className="settings-card">
              
              {/* ✅ SPRACHE - MIT 3 OPTIONEN */}
              <div className="settings-item" onClick={() => setShowLanguageSelect(!showLanguageSelect)}>
                <div className="settings-item-left">
                  <div className="settings-item-icon orange">🌍</div>
                  <div>
                    <div className="settings-item-label">{getText('language')}</div>
                  </div>
                </div>
                <div className="settings-item-right">
                  <span className="settings-item-value">
                    {currentLanguage?.flag} {currentLanguage?.name || 'Deutsch'}
                  </span>
                  <span className="settings-item-arrow">{showLanguageSelect ? '▼' : '›'}</span>
                </div>
              </div>
              
              {/* ✅ DROPDOWN MIT ALLEN 3 SPRACHEN */}
              {showLanguageSelect && (
                <div className="settings-language-dropdown">
                  {LANGUAGES.map((lang) => (
                    <button 
                      key={lang.code}
                      className={`settings-language-option ${language === lang.code ? 'active' : ''}`}
                      onClick={() => handleLanguageChange(lang.code)}
                    >
                      {lang.flag} {lang.name}
                      {language === lang.code && <span style={{ marginLeft: 'auto' }}>✓</span>}
                    </button>
                  ))}
                </div>
              )}

              <div className="settings-item" onClick={handleDarkModeToggle}>
                <div className="settings-item-left">
                  <div className="settings-item-icon gray">🌙</div>
                  <div>
                    <div className="settings-item-label">{getText('darkMode')}</div>
                  </div>
                </div>
                <div className="settings-item-right">
                  <div className={`settings-toggle ${darkMode ? 'active' : ''}`} />
                </div>
              </div>
            </div>
          </div>

          {/* HILFE & SUPPORT */}
          <div className="settings-section">
            <div className="settings-section-title">{getText('helpSupport')}</div>
            <div className="settings-card">

              <div className="settings-item" onClick={() => handleNavigateToPage('feedback')}>
                <div className="settings-item-left">
                  <div className="settings-item-icon purple">💬</div>
                  <div>
                    <div className="settings-item-label">{getText('feedback')}</div>
                  </div>
                </div>
                <div className="settings-item-right">
                  <span className="settings-item-arrow">›</span>
                </div>
              </div>

              <div className="settings-item" onClick={() => handleNavigateToPage('datenschutz')}>
                <div className="settings-item-left">
                  <div className="settings-item-icon gray">📄</div>
                  <div>
                    <div className="settings-item-label">{getText('legal')}</div>
                    <div className="settings-item-sublabel">{getText('legalDesc')}</div>
                  </div>
                </div>
                <div className="settings-item-right">
                  <span className="settings-item-arrow">›</span>
                </div>
              </div>
            </div>
          </div>

          {/* KONTO LÖSCHEN */}
          <div className="settings-section">
            <div className="settings-section-title" style={{ color: '#DC2626' }}>
              {getText('deleteAccountSection')}
            </div>
            <div className="settings-card" style={{ border: '1px solid #FECACA' }}>
              
              {deleteStep === 0 && (
                <div 
                  className="settings-item" 
                  onClick={() => setDeleteStep(1)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="settings-item-left">
                    <div className="settings-item-icon" style={{ background: '#DC2626' }}>🗑️</div>
                    <div>
                      <div className="settings-item-label" style={{ color: '#DC2626' }}>
                        {getText('deleteAccount')}
                      </div>
                      <div className="settings-item-sublabel">{getText('deleteAccountDesc')}</div>
                    </div>
                  </div>
                  <div className="settings-item-right">
                    <span className="settings-item-arrow">›</span>
                  </div>
                </div>
              )}

              {/* STEP 1: Warnung */}
              {deleteStep === 1 && (
                <div style={{ padding: '20px' }}>
                  <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '8px' }}>⚠️</div>
                    <div style={{ fontWeight: 700, color: '#DC2626', fontSize: '1.1rem' }}>
                      {getText('deleteWarningTitle')}
                    </div>
                  </div>
                  
                  <div style={{ color: '#374151', fontSize: '0.9rem', marginBottom: '12px' }}>
                    {getText('deleteWarningText')}
                  </div>
                  
                  <ul style={{ margin: '0 0 20px 0', padding: '0 0 0 20px', color: '#6B7280', fontSize: '0.85rem' }}>
                    {getText('deleteWarningList').map((item, idx) => (
                      <li key={idx} style={{ marginBottom: '4px' }}>❌ {item}</li>
                    ))}
                  </ul>
                  
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => setDeleteStep(2)}
                      style={{
                        flex: 1,
                        padding: '12px',
                        background: '#DC2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      {getText('deleteWarningContinue')}
                    </button>
                    <button
                      onClick={handleCancelDelete}
                      style={{
                        padding: '12px 24px',
                        background: '#F3F4F6',
                        color: '#374151',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: 500,
                        cursor: 'pointer'
                      }}
                    >
                      {getText('cancel')}
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 2: Finale Bestätigung */}
              {deleteStep === 2 && (
                <div style={{ padding: '20px' }}>
                  <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '8px' }}>🗑️</div>
                    <div style={{ fontWeight: 700, color: '#DC2626', fontSize: '1.1rem' }}>
                      {getText('deleteConfirmTitle')}
                    </div>
                  </div>
                  
                  <div style={{ color: '#6B7280', fontSize: '0.85rem', marginBottom: '16px', textAlign: 'center' }}>
                    {getText('deleteConfirmText')}
                  </div>
                  
                  <input
                    type="text"
                    value={deleteInput}
                    onChange={(e) => setDeleteInput(e.target.value.toUpperCase())}
                    placeholder={getDeleteConfirmWord()}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #FECACA',
                      borderRadius: '8px',
                      fontSize: '1.1rem',
                      textAlign: 'center',
                      marginBottom: '8px',
                      boxSizing: 'border-box',
                      fontWeight: 600,
                      letterSpacing: '2px'
                    }}
                  />
                  <div style={{ fontSize: '0.75rem', color: '#6B7280', marginBottom: '16px', textAlign: 'center' }}>
                    {getText('deleteConfirmInput')}
                  </div>
                  
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={handleDeleteAccount}
                      disabled={deleteInput !== getDeleteConfirmWord() || deleteLoading}
                      style={{
                        flex: 1,
                        padding: '12px',
                        background: deleteInput === getDeleteConfirmWord() && !deleteLoading ? '#DC2626' : '#FCA5A5',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: 600,
                        cursor: deleteInput === getDeleteConfirmWord() && !deleteLoading ? 'pointer' : 'not-allowed'
                      }}
                    >
                      {deleteLoading ? '⏳ ...' : `🗑️ ${getText('deleteAccount')}`}
                    </button>
                    <button
                      onClick={() => { setDeleteStep(1); setDeleteInput(''); }}
                      style={{
                        padding: '12px 20px',
                        background: '#F3F4F6',
                        color: '#374151',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      {getText('back')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* LOGOUT */}
          <div className="settings-action-buttons">
            <button className="settings-btn settings-btn-danger" onClick={handleLogout}>
              <span>🚪</span>
              <span>{getText('logout')}</span>
            </button>
          </div>

          {/* ✅ v3: Planivio VERSION INFO */}
          <div className="settings-version-info" style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: '#F3F4F6',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '0.8rem',
              color: '#6B7280'
            }}>
              <span>🏠</span>
              <span style={{ 
                // ✅ v3: Planivio Teal
                background: '#4A90A4', 
                color: 'white', 
                padding: '2px 8px', 
                borderRadius: '10px',
                fontSize: '0.7rem',
                fontWeight: 600
              }}>
                v{APP_VERSION}
              </span>
            </div>
            <div style={{ marginTop: '8px', fontSize: '0.7rem', color: '#9CA3AF' }}>
              {getText('build')}: {BUILD_NUMBER} • {BUILD_ENV}
            </div>
          </div>

        </div>
      </div>
      
      {/* Account Settings Modal */}
      <AccountSettings
        isOpen={showAccountSettings}
        onClose={() => setShowAccountSettings(false)}
        language={language}
        user={currentUser}
      />
    </div>
  );
};

export default SettingsModal;


